import { useId, useState, type ReactNode, type Ref } from 'react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/dexie.js';
import { lecturaDeCodigo } from '../lib/catalogo.js';
import { useT } from '../i18n/index.js';
import { useNombreProducto } from '../i18n/productos.js';
import { BotonEscanear, Escaner } from './Escaner.js';
import { Ico } from './Iconos.js';

/**
 * A barcode field: typed, sent by a USB scanner, or read with the camera.
 *
 * It says straight away when the code already belongs to another product, with a way to open
 * that one — because a code that is already somebody's almost always means the goods are
 * already in the catalogue, and the product about to be created is a second copy of them. It
 * says so and does not refuse: the person holding the can knows better than the app. Once they
 * have said it is another product with the same code (`compartido`: a factory printed one code on
 * two), it says who else carries it instead, without the warning.
 */
export function CampoCodigo({
  valor,
  onCambio,
  etiqueta,
  ayuda,
  propio,
  compartido = false,
  accion,
  onEnter,
  onEscaneado,
  ref,
}: {
  valor: string;
  onCambio: (codigo: string) => void;
  etiqueta?: string;
  ayuda?: ReactNode;
  /** The product this code is for, which may already carry it without that being news. */
  propio?: string;
  /** Said to be on a different product too, on purpose: who else carries it, calmly. */
  compartido?: boolean;
  /** Beside the camera button: the field's own «Agregar», where it has one. */
  accion?: ReactNode;
  /** Enter in the box. A USB scanner presses it after every code. */
  onEnter?: () => void;
  /** After the camera has filled the box with this code: to move on to the next field, say. */
  onEscaneado?: (codigo: string) => void;
  ref?: Ref<HTMLInputElement>;
}) {
  const t = useT();
  const nombre = useNombreProducto();
  const id = useId();
  const [escaneando, setEscaneando] = useState(false);
  const productos = useLiveQuery(() => db.productos.toArray(), [], []);
  const unidades = useLiveQuery(() => db.unidades.toArray(), [], []);
  const deOtro = valor.trim()
    ? lecturaDeCodigo(productos, unidades, valor, { inactivos: true }).find((h) => h.producto.id !== propio)?.producto
    : undefined;

  return (
    <div>
      <label htmlFor={id} className="etiqueta">
        {etiqueta ?? t('producto.codigoBarras')}
      </label>
      {/* The box keeps room for thirteen digits; an «Agregar» that no longer fits beside it wraps. */}
      <div className="flex flex-wrap gap-2">
        <input
          ref={ref}
          id={id}
          className="campo tabular min-w-40 flex-1 basis-40"
          value={valor}
          onChange={(e) => onCambio(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && onEnter) {
              e.preventDefault();
              e.stopPropagation();
              onEnter();
            }
          }}
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          // A barcode field: a scanner's code is left in it, not sent to the screen's search.
          data-codigo
        />
        <BotonEscanear onClick={() => setEscaneando(true)} />
        {accion}
      </div>
      {deOtro && compartido ? (
        <p className="mt-1.5 text-sm text-slate-400" data-compartido>
          {t(deOtro.activo ? 'codigo.compartido' : 'codigo.compartidoDesactivado', { producto: nombre(deOtro) })}
        </p>
      ) : deOtro ? (
        <p className="mt-1.5 text-sm text-amber-300">
          {t(deOtro.activo ? 'codigo.yaEsDe' : 'codigo.yaEsDeDesactivado', { producto: nombre(deOtro) })}{' '}
          <Link
            to="/productos/$productoId"
            params={{ productoId: deOtro.id }}
            className="btn-texto gap-1 text-amber-200"
          >
            {t('codigo.verlo')}
            <Ico icono="adelante" className="size-4" />
          </Link>
        </p>
      ) : (
        ayuda && <span className="mt-1.5 block text-xs text-slate-500">{ayuda}</span>
      )}

      {escaneando && (
        <Escaner
          titulo={t('escaner.tituloCodigo')}
          onCerrar={() => setEscaneando(false)}
          onLeido={({ codigo }) => {
            onCambio(codigo);
            setEscaneando(false);
            onEscaneado?.(codigo);
            return 'bien';
          }}
        />
      )}
    </div>
  );
}
